import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationKey } from '../../i18n/translationKeys';
import { useSyncStore } from '../../stores/syncStore';
import { usePartialSync } from '../../hooks/usePartialSync';
import { useFillReached } from '../../hooks/useFillReached';
import { useRefreshOnVisible } from '../../hooks/useRefreshOnVisible';
import { formatRelativeTime } from '../../utils/formatters';
import { LinkButton } from '../../ui/LinkButton/LinkButton';
import { AnimatedEllipsis } from '../../ui/AnimatedEllipsis/AnimatedEllipsis';

const TICK_INTERVAL_MS = 60 * 1000;

export interface SyncFreshnessLineProps {
    /** data-testid for the line. Required so each page names its own — E2E selectors depend on it. */
    testId: string;
    /** data-testid for the refresh action. */
    refreshTestId: string;
    /** Extra classes on the line — spacing is the caller's business, not the component's. */
    className?: string;
    /** Called synchronously when the button is clicked, before `trigger()`. */
    onRefreshClick?: () => void;
    /** Replaces the whole line, Refresh included, with this text plus an animated ellipsis. Refresh is
     * hidden because the override reports something other than a tenant sync. */
    statusOverride?: string;
    /** TEMPORARY pilot full resync: remove with the admin full-resync endpoint. */
    onFullResyncRequest?: () => void;
}

/**
 * "Dernière mise à jour il y a 15 min — Rafraîchir".
 *
 * Rendered under the greeting on Home and above the relance on the detail page. Shared rather than
 * duplicated so both stay on one behaviour: the label ages on its own via a one-minute tick, and the
 * refresh action is a partial sync. Shift+click calls `onFullResyncRequest` instead, when given.
 *
 * It reports freshness and triggers a sync; it does not decide what a page reloads afterwards. Each
 * caller watches `usePartialSync().syncing` for that — Home reloads its lists, the detail page its
 * relance.
 */
export const SyncFreshnessLine: React.FC<SyncFreshnessLineProps> = ({ testId, refreshTestId, className, onRefreshClick, statusOverride, onFullResyncRequest }) => {
    const { t, i18n } = useTranslation();
    const lastSyncedAt = useSyncStore(s => s.lastSyncedAt);
    const { trigger, syncing, canSync, progress } = usePartialSync();
    const fillReached = useFillReached(syncing, progress?.phase);
    const [now, setNow] = useState(() => new Date());

    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), TICK_INTERVAL_MS);
        return () => clearInterval(id);
    }, []);

    // Browsers throttle (and can stall) timers while a tab or panel is hidden, so a panel left open in
    // the background comes back showing a value that stopped ageing. Recompute the elapsed time on the
    // way back rather than making the user wait out the next tick.
    //
    // Only the clock: re-reading lastSyncedAt belongs to the page, which reloads it together with its
    // own data. Refreshing the timestamp alone here would show a fresh date above stale content — worse
    // than showing both stale.
    useRefreshOnVisible(() => setNow(new Date()));

    // A tenant that has never synced is handled by the initial-sync flow, not by this label.
    if (!lastSyncedAt) return null;

    const relative = formatRelativeTime(lastSyncedAt, i18n?.language ?? 'fr', now);
    // The relances keep filling in after a real-time sync has ended; `filling` carries that past `syncing`.
    const isFilling = (syncing && fillReached) || progress?.filling === true;
    const label = statusOverride ?? (isFilling
        ? t(TranslationKey.Home.LastSync.Filling, { completed: progress?.completed ?? 0, expected: progress?.expected ?? 0 })
        : t(TranslationKey.Home.LastSync.Label, { relative: t(relative.key, relative.options) }));

    return (
        <p className={`text-m font-medium text-content-subtle${className ? ` ${className}` : ''}`} data-testid={testId}>
            {label}
            {statusOverride ? (
                <AnimatedEllipsis />
            ) : (
                <>
                    {' — '}
                    <LinkButton
                        data-testid={refreshTestId}
                        onClick={(e) => {
                            // TEMPORARY pilot full resync: remove with the admin full-resync endpoint.
                            if (e.shiftKey && onFullResyncRequest) {
                                onFullResyncRequest();
                                return;
                            }
                            onRefreshClick?.();
                            void trigger();
                        }}
                        disabled={syncing || !canSync}
                        variant="navigation"
                        weight="medium"
                    >
                        {t(TranslationKey.Home.LastSync.Refresh)}
                    </LinkButton>
                </>
            )}
        </p>
    );
};
